import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { FiChevronDown, FiChevronUp, FiFileText } from "react-icons/fi";
import { IoIosAdd, IoIosRemove } from "react-icons/io";
import { IoAlertCircle, IoWarningOutline } from "react-icons/io5";
import { MdOutlineEdit, MdOutlineVisibility } from "react-icons/md";
import CustomAddModal from "../../../components/Common/CustomAddModal";
import CustomShowModal from "../../../components/Common/CustomShowModal";
import { getCurrentUserId, getCurrentUserOrganizationId, toastError, toastSuccess } from "../../../utils";
import { getAddressDetails } from "../AddressBook/actions";
import { generateFuelSupplyRequest, updateFuelSupplyRequest } from "../FuelSupply/actions";

const FuelSupplyReqModel = ({ isReadOnly = false, data, uniqIds, vesselContractDetails, refetch, operationId }) => {
    const [expandedRows, setExpandedRows] = useState(null);
    const [counterPartyDetails, setCounterPartyDetails] = useState();
    const [formData, setFormData] = useState({});
    const scrollRef = useRef(null);

    const [open, setOpen] = useState({
        mode: "add",
        isOpen: false,
        isWarning: false,
    });

    const transformAddressItems = (list = []) => {
        return list.map(item => {
            let fullName = "";

            if (item.type === "Individual") {
                const { individual_firstName = "", individual_middleName = "", individual_lastName = "" } = item;
                fullName = [individual_firstName, individual_middleName, individual_lastName].filter(Boolean).join(" ");
            } else if (item.type === "Organisation") {
                fullName = item.organisation_fullName || "";
            }

            return {
                _id: item._id,
                type: item.type,
                fullName,
            };
        });
    };

    const fetchCounterPartyDetails = async () => {
        const userId = getCurrentUserId();

        try {
            const userRes = await getAddressDetails({ userId, addressBookType: "organization", perPage: 100 })
            setCounterPartyDetails(transformAddressItems(userRes?.data?.list));
        } catch (error) {
            console.error("Failed to fetch counter party details:", error);
            setCounterPartyDetails([]);
        }
    };

    useEffect(() => {
        fetchCounterPartyDetails();
    }, []);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };
    const toggleRow = (cargoId) => {
        setExpandedRows(prev => {
            if (prev === cargoId) {
                return null;
            }
            return cargoId;
        });
    };
    let { cargoList = [], FuelSupplyRequests: fuelSupplyRequests = [] } = data || {};

    const [FuelSupplyRequests, setFuelSupplyRequests] = useState(fuelSupplyRequests);

    const addItemAtIndex = (field, index) => {
        setFormData(prev => {
            const updated = [...(prev[field] ?? [])];
            updated.splice(index + 1, 0, { name: "", type: "", id: "" });
            return { ...prev, [field]: updated };
        });
    };

    const updateItemAtIndex = (field, index, value) => {
        const counterParty = counterPartyDetails;
        setFormData(prev => {
            const currentList = [...(prev[field] ?? [])];
            const selected = counterParty?.find(item => item._id === value) ?? { type: "", _id: "" };
            currentList[index] = {
                name: selected.fullName,
                type: selected.type,
                id: selected._id,
            };
            return { ...prev, [field]: currentList };
        });
    };

    const deleteItemAtIndex = (field, index) => {
        setFormData(prev => {
            const currentList = prev[field] ?? [];
            if (currentList.length <= 1) return prev;
            const updated = [...currentList];
            updated.splice(index, 1);
            return { ...prev, [field]: updated };
        });
    };

    const getAvailableCounterParties = (field, currentIndex) => {
        const selectedItems = formData?.[field] || [];
        const allCounterParties = counterPartyDetails || [];

        const currentSelectedId = selectedItems[currentIndex]?.id;

        const selectedIds = selectedItems
            .map((item, index) => (index !== currentIndex ? item.id : null))
            .filter((id) => id != null);

        let available = allCounterParties.filter((item) =>
            !selectedIds.includes(item._id) || item._id === currentSelectedId
        );

        if (currentSelectedId && !available.some(item => item._id === currentSelectedId)) {
            const currentItem = selectedItems[currentIndex];
            available = [{
                _id: currentSelectedId,
                fullName: currentItem.name || "Unknown Agent",
                type: currentItem.type || "Unknown Type"
            }, ...available];
        }

        return available;
    };

    const hasData = counterPartyDetails?.length > 0;

    const onClickAddNewRequest = (cargo) => {
        const vesselDetails = vesselContractDetails?.vesselDetails || {};
        const vesselName = vesselDetails?.vessel && JSON.parse(vesselDetails?.vessel)?.vesselName || "";
        const intialData = {
            agents: [{ name: "", type: "", id: "" }],
            vesselName,
            cargoListId: cargo?._id,
            voyageNo: uniqIds?.voyageNumber || "",
            opsId: uniqIds?.opsId,
            vesselCode: uniqIds?.vesselId,
            port: cargo?.port || "",
        }
        setFormData(intialData);
        setOpen({
            mode: "add",
            isOpen: true,
        });
    }

    const handleGenrateNewRequest = async () => {
        try {
            const cleanedData = {
                ...formData,
                isPosted: false,
                userId: getCurrentUserId(),
                operationId,
                organizationId: getCurrentUserOrganizationId(),
            };
            const res = await generateFuelSupplyRequest({ id: operationId }, cleanedData)
            setFuelSupplyRequests(res.data)
            setOpen({
                mode: "add",
                isOpen: false,
            });
            toggleRow(cleanedData.cargoListId);
            toastSuccess("Fuel supply request successfully created! You can now edit or post it.");
        } catch (error) {
            toastError(error);
        }
    }

    const handleUpdateRequest = async () => {
        try {
            const res = await updateFuelSupplyRequest({ id: operationId }, formData)
            setFuelSupplyRequests(res.data)
            setOpen({
                mode: "edit",
                isOpen: false,
            });
            toastSuccess("Fuel Supply Request Updated Successfully");
        } catch (error) {
            toastError(error);
        }
    }

    const handleEdit = (item) => {
        setFormData(item);
        const viewMode = item.isPosted;
        setOpen({
            mode: viewMode ? "view" : "edit",
            isOpen: true,
        });
    }

    const handlePost = async (item) => {
        if (!open.isWarning) {
            setOpen((prev) => ({
                ...prev,
                isWarning: true,
            }));
            setFormData(item);
        } else {
            const res = await updateFuelSupplyRequest({ id: operationId }, { ...formData, isPosted: true })
            setFuelSupplyRequests(res.data)
            setOpen({
                mode: "add",
                isOpen: false,
            });
            toastSuccess("Fuel Supply Request Updated Successfully");
        }
    }

    return (
        <div className="overflow-hidden border border-[#cbd5e1] rounded-none shadow-none bg-white scroll-thin">
            <table className="min-w-full border-collapse text-[11px] font-medium">
                <thead className="bg-[#e2e8f0] text-[#1e293b]">
                    <tr>
                        <th
                            scope="col"
                            className="px-2 py-1 text-left font-medium text-[11px] text-[#1e293b] w-8 border-r border-b border-[#cbd5e1]"
                        >
                        </th>
                        <th
                            scope="col"
                            className="px-2 py-1 text-left font-medium text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1]"
                        >
                            Port
                        </th>
                        <th
                            scope="col"
                            className="px-2 py-1 text-left font-medium text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1]"
                        >
                            Operation
                        </th>
                        <th
                            scope="col"
                            className="px-2 py-1 text-left font-medium text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1]"
                        >
                            Departure Time
                        </th>
                        <th
                            scope="col"
                            className="px-2 py-1 text-left font-medium text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1]"
                        >
                            Arrival Time
                        </th>
                        <th
                            scope="col"
                            className="px-2 py-1 text-center font-medium text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1]"
                        >
                            Fuel Requests
                        </th>
                        {!isReadOnly && (
                            <th
                                scope="col"
                                className="px-2 py-1 text-right font-medium text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1]"
                            >
                                Actions
                            </th>
                        )}
                    </tr>
                </thead>
                <tbody className="bg-white">
                    {cargoList.length === 0 ? (
                        <tr>
                            <td colSpan={isReadOnly ? 6 : 7} className="text-center py-4 text-slate-400 italic text-[11px] font-medium border-r border-b border-[#cbd5e1]">
                                No cargo items found.
                            </td>
                        </tr>
                    ) : (
                        cargoList.map((cargo, index) => {
                            const uniqueId = cargo._id || cargo.id || index;
                            const isExpanded = expandedRows === uniqueId;
                            const relatedFuelRequests = FuelSupplyRequests.filter(
                                (req) => (req.cargoListId === cargo._id) || (req.cargoListId === cargo.id)
                            );

                            return (
                                <React.Fragment key={uniqueId}>
                                    {/* Main Row */}
                                    <tr
                                        className="h-6 hover:bg-slate-50 transition-colors cursor-pointer border-b border-[#cbd5e1]"
                                        onClick={() => toggleRow(uniqueId)}
                                        aria-expanded={isExpanded}
                                        aria-controls={`fuel-requests-${uniqueId}`}
                                    >
                                        <td className="px-2 py-0.5 whitespace-nowrap text-slate-500 border-r border-b border-[#cbd5e1]">
                                            {isExpanded ? (
                                                <FiChevronUp className="h-3.5 w-3.5" />
                                            ) : (
                                                <FiChevronDown className="h-3.5 w-3.5" />
                                            )}
                                        </td>
                                        <td className="px-2 py-0.5 whitespace-nowrap text-[11px] font-medium text-slate-800 border-r border-b border-[#cbd5e1]">
                                            {cargo.port || "N/A"}
                                        </td>
                                        <td className="px-2 py-0.5 whitespace-nowrap text-[11px] font-medium text-slate-700 border-r border-b border-[#cbd5e1]">
                                            {cargo.operation || "N/A"}
                                        </td>
                                        <td className="px-2 py-0.5 whitespace-nowrap text-[11px] font-medium text-slate-600 border-r border-b border-[#cbd5e1]">
                                            {cargo.departureTime || "N/A"}
                                        </td>
                                        <td className="px-2 py-0.5 whitespace-nowrap text-[11px] font-medium text-slate-600 border-r border-b border-[#cbd5e1]">
                                            {cargo.arrivalTime || "N/A"}
                                        </td>
                                        <td className="px-2 py-0.5 whitespace-nowrap text-center text-[11px] font-medium text-slate-900 border-r border-b border-[#cbd5e1]">
                                            <span className="px-1.5 py-0.5 rounded-none bg-[#f0f4f9] border border-[#cbd5e1] text-slate-800 text-[10px] font-medium">
                                                {relatedFuelRequests.length}
                                            </span>
                                        </td>
                                        {!isReadOnly && (
                                            <td className="px-2 py-0.5 whitespace-nowrap text-right font-medium border-r border-b border-[#cbd5e1]">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onClickAddNewRequest(cargo)
                                                    }}
                                                    className="inline-flex items-center gap-1 h-5 px-2 text-[11px] font-medium rounded-none bg-[#313C4C] text-white hover:bg-[#1e2632] transition cursor-pointer"
                                                >
                                                    <FiFileText className="h-3 w-3" />
                                                    <span>New Request</span>
                                                </button>
                                            </td>
                                        )}
                                    </tr>

                                    {/* Expanded Row */}
                                    {isExpanded && (
                                        <tr id={`fuel-requests-${uniqueId}`} className="bg-slate-50/50">
                                            <td colSpan={isReadOnly ? 6 : 7} className="p-2 border-b border-[#cbd5e1]">
                                                {relatedFuelRequests.length > 0 ? (
                                                    <div className="overflow-hidden border border-[#cbd5e1] rounded-none shadow-none bg-white">
                                                        <div className="px-2.5 py-1 bg-[#e2e8f0] border-b border-[#cbd5e1]">
                                                            <h3 className="text-[11px] font-medium text-[#1e293b] m-0">Associated Fuel Requests</h3>
                                                        </div>
                                                        <div className="overflow-x-auto scroll-thin">
                                                            <table className="min-w-full border-collapse text-[11px] font-medium">
                                                                <thead className="bg-[#e2e8f0] text-[#1e293b] border-b border-[#cbd5e1]">
                                                                    <tr>
                                                                        <th scope="col" className="px-2 py-1 text-left font-medium text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1]">Request ID</th>
                                                                        <th scope="col" className="px-2 py-1 text-left font-medium text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1]">Requested Date</th>
                                                                        <th scope="col" className="px-2 py-1 text-left font-medium text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1]">Request Type</th>
                                                                        <th scope="col" className="px-2 py-1 text-left font-medium text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1]">Fuel Usage Type</th>
                                                                        <th scope="col" className="px-2 py-1 text-left font-medium text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1]">Specification</th>
                                                                        <th scope="col" className="px-2 py-1 text-left font-medium text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1]">Supply QTY</th>
                                                                        <th scope="col" className="px-2 py-1 text-left font-medium text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1]">Action</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody className="bg-white">
                                                                    {relatedFuelRequests.map((item, idx) => {
                                                                        return (<tr key={item._id || item.id || idx} className="h-6 hover:bg-slate-50 transition-colors">
                                                                            <td className="px-2 py-0.5 whitespace-nowrap text-[11px] font-medium text-slate-800 hover:underline cursor-pointer border-r border-b border-[#cbd5e1]">
                                                                                {item.requestId || item.id || "N/A"}
                                                                            </td>
                                                                            <td className="px-2 py-0.5 whitespace-nowrap text-[11px] font-medium text-slate-600 border-r border-b border-[#cbd5e1]">
                                                                                {item.requestedDate && moment(item.requestedDate).isValid()
                                                                                    ? moment(item.requestedDate).format("DD MMM YYYY")
                                                                                    : "N/A"}
                                                                            </td>
                                                                            <td className="px-2 py-0.5 whitespace-nowrap text-[11px] font-medium text-slate-600 border-r border-b border-[#cbd5e1]">
                                                                                <span className="inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded-none bg-[#f0f4f9] text-slate-700 border border-[#cbd5e1]">
                                                                                    {item.requestType || "N/A"}
                                                                                </span>
                                                                            </td>
                                                                            <td className="px-2 py-0.5 whitespace-nowrap text-[11px] font-medium text-slate-600 border-r border-b border-[#cbd5e1]">
                                                                                {item.fuelUsageType || "N/A"}
                                                                            </td>
                                                                            <td className="px-2 py-0.5 whitespace-nowrap text-[11px] font-medium text-slate-600 border-r border-b border-[#cbd5e1]">
                                                                                {item.fuelSpecification || "N/A"}
                                                                            </td>
                                                                            <td className="px-2 py-0.5 whitespace-nowrap text-[11px] font-medium text-slate-800 border-r border-b border-[#cbd5e1]">
                                                                                {item.suppliedQty || "N/A"}
                                                                            </td>
                                                                            <td className="px-2 py-0.5 flex gap-1 items-center whitespace-nowrap text-[11px] font-medium border-r border-b border-[#cbd5e1]">
                                                                                <button onClick={() => handleEdit(item)} className="p-0.5 rounded-none text-slate-700 hover:bg-slate-100 transition cursor-pointer" title={item.isPosted ? "View request" : "Edit request"}>
                                                                                    {(item.isPosted || isReadOnly) ? <MdOutlineVisibility size={14} /> : <MdOutlineEdit size={14} />}
                                                                                </button>
                                                                                <button
                                                                                    disabled={item.isPosted || isReadOnly}
                                                                                    onClick={() => handlePost(item)}
                                                                                    className={`h-5 px-1.5 rounded-none text-[10px] font-medium transition ${(item.isPosted || isReadOnly)
                                                                                        ? "text-slate-400 cursor-not-allowed bg-slate-100 border border-slate-200"
                                                                                        : "text-slate-800 bg-[#f0f4f9] hover:bg-slate-200 border border-[#cbd5e1] cursor-pointer"
                                                                                        }`}
                                                                                >
                                                                                    Post
                                                                                </button>
                                                                            </td>
                                                                        </tr>);
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-center py-3 bg-white rounded-none border border-[#cbd5e1]">
                                                        <h3 className="text-[11px] font-medium text-slate-700 m-0">No fuel requests</h3>
                                                        <p className="mt-0.5 text-[11px] text-slate-400 italic">There are no fuel requests associated with this cargo.</p>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })
                    )}
                </tbody>
            </table>

            <CustomAddModal
                open={open.isOpen}
                handleClose={() => setOpen({
                    mode: "add",
                    isOpen: false,
                }) && setFormData({})}
                title="Fuel Supply Request"
                maxWidth="md"
                detailsTitle="View Fuel Supply Request"
                isDetailView={open.mode === "view" || isReadOnly}
                onSubmitConfirm={open.mode === "add" ? handleGenrateNewRequest : handleUpdateRequest}
                saveLabel={open.mode === "add" ? "Draft Request" : "Update Request"}
            >
                <div className="space-y-2.5 text-[11px] font-medium">
                    <div className="grid grid-cols-3 gap-2.5">
                        <div>
                            <div className="mb-2">
                                <label className="block text-[11px] font-medium text-slate-700 mb-0.5">Request Type</label>
                                <select
                                    value={formData.requestType}
                                    onChange={(e) => handleInputChange('requestType', e.target.value)}
                                    className="w-full h-7 px-2 py-1 border border-slate-300 bg-white text-slate-800 rounded-none text-[11px] font-medium focus:outline-none focus:border-[#313C4C]"
                                >
                                    <option value="">Select request type</option>
                                    <option value="Firm Request">Firm Request</option>
                                    <option value="Intimation">Intimation</option>
                                    <option value="Hedging Request">Hedging Request</option>
                                </select>
                            </div>

                            {[{
                                label: "Vessel Name",
                                value: "vesselName",
                            }, {
                                label: "Vessel Code",
                                value: "vesselCode",
                            }, {
                                label: "Ops ID",
                                value: "opsId",
                            }, {
                                label: "Voyage Number",
                                value: "voyageNo",
                            }].map(field => (
                                <div key={field.value} className="mb-2">
                                    <label className="block text-[11px] font-medium text-slate-700 mb-0.5">{field.label}</label>
                                    <input
                                        type="text"
                                        value={formData[field.value] || ''}
                                        disabled
                                        className="w-full h-7 px-2 py-1 border border-slate-200 bg-slate-100 text-slate-600 rounded-none text-[11px] font-medium cursor-not-allowed"
                                        placeholder=""
                                    />
                                </div>
                            ))}
                        </div>

                        <div>
                            <div className="mb-2">
                                <label className="block text-[11px] font-medium text-slate-700 mb-0.5">Port</label>
                                <input
                                    type="text"
                                    value={formData.port || ''}
                                    disabled
                                    className="w-full h-7 px-2 py-1 border border-slate-200 bg-slate-100 text-slate-600 rounded-none text-[11px] font-medium cursor-not-allowed"
                                    placeholder=""
                                />
                            </div>
                            <div className="mb-2">
                                <label className="block text-[11px] font-medium text-slate-700 mb-0.5">Fuel Usage Type</label>
                                <select
                                    value={formData.fuelUsageType}
                                    onChange={(e) => handleInputChange('fuelUsageType', e.target.value)}
                                    className="w-full h-7 px-2 py-1 border border-slate-300 bg-white text-slate-800 rounded-none text-[11px] font-medium focus:outline-none focus:border-[#313C4C]"
                                >
                                    <option value="">Select fuel usage type</option>
                                    <option value="Main">Main</option>
                                    <option value="ECA">ECA</option>
                                    <option value="Light Fuel (MGO)">Light Fuel (MGO)</option>
                                </select>
                            </div>
                            {[
                                { label: 'Fuel Specification', field: 'fuelSpecification' },
                                { label: 'Requested Quantity (MT)', field: 'reqQuantity', type: 'number' },
                                { label: 'Committed Price', field: 'committedPrice', type: 'number' },
                            ].map(({ label, field, type = 'text' }) => (
                                <div key={field} className="mb-2">
                                    <label className="block text-[11px] font-medium text-slate-700 mb-0.5">{label}</label>
                                    <input
                                        type={type}
                                        value={formData[field] || ''}
                                        onChange={(e) => handleInputChange(field, e.target.value)}
                                        className="w-full h-7 px-2 py-1 border border-slate-300 bg-white text-slate-800 rounded-none text-[11px] font-medium focus:outline-none focus:border-[#313C4C]"
                                        placeholder={`Enter ${label.toLowerCase()}`}
                                    />
                                </div>
                            ))}
                        </div>

                        <div>
                            {[
                                { label: 'Supplied Qty', field: 'suppliedQty', type: 'number' },
                                { label: 'Purchase Price', field: 'purchasePrice', type: 'number' },
                                { label: 'Difference', field: 'difference', type: 'number' },
                                { label: 'Bunker Manager', field: 'bunkerManager' },
                            ].map(({ label, field, type = 'text' }) => (
                                <div key={field} className="mb-2">
                                    <label className="block text-[11px] font-medium text-slate-700 mb-0.5">{label}</label>
                                    <input
                                        type={type}
                                        value={formData[field] || ''}
                                        onChange={(e) => handleInputChange(field, e.target.value)}
                                        className="w-full h-7 px-2 py-1 border border-slate-300 bg-white text-slate-800 rounded-none text-[11px] font-medium focus:outline-none focus:border-[#313C4C]"
                                        placeholder={`Enter ${label.toLowerCase()}`}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex-1">
                        <div
                            ref={scrollRef}
                            style={{ overscrollBehavior: 'contain' }}
                            className="flex-1 border border-slate-200 rounded-none max-h-[160px] overflow-y-auto p-2 scroll-thin bg-slate-50/50"
                        >
                            <div className="flex justify-center items-center w-full mb-1.5">
                                <h2 className="block text-[11px] text-center font-medium uppercase tracking-wider text-slate-700 m-0">Agents</h2>
                            </div>

                            {!hasData ? (
                                <div className="flex flex-col items-center justify-center py-2 px-3 text-center">
                                    <h3 className="text-[11px] font-medium text-slate-700 mb-0.5">
                                        No Agents found
                                    </h3>
                                    <p className="text-[11px] text-slate-400 mb-2 max-w-xs leading-tight italic">
                                        You don't have any Agents in your address book yet.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => (window.location.href = '/addressBook')}
                                        className="inline-flex items-center h-6 px-2.5 text-[11px] font-medium text-white bg-[#313C4C] rounded-none hover:bg-[#1e2632] transition-colors cursor-pointer"
                                    >
                                        Create Agent
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-1">
                                    {(formData?.agents?.length ? formData?.agents : [{ name: "", type: "" }]).map(
                                        (item, index) => (
                                            <div key={item.id || `${'agents'}-${index}`}
                                                className="flex items-center gap-1">
                                                {!isReadOnly && <button
                                                    type="button"
                                                    disabled={getAvailableCounterParties('agents', index).length === formData?.agents?.length}
                                                    onClick={() => addItemAtIndex('agents', index)}
                                                    className="h-7 w-7 text-slate-600 hover:text-slate-900 border border-slate-300 rounded-none bg-white flex items-center justify-center cursor-pointer text-sm"
                                                    title="Add below"
                                                >
                                                    <IoIosAdd />
                                                </button>}
                                                <select
                                                    required
                                                    disabled={isReadOnly}
                                                    value={item.id}
                                                    onChange={(e) => updateItemAtIndex('agents', index, e.target.value)}
                                                    className="flex-1 h-7 px-2 py-1 text-[11px] font-medium border border-slate-300 rounded-none focus:outline-none focus:border-[#313C4C] bg-white"
                                                >
                                                    <option value="">Select Agent</option>
                                                    {getAvailableCounterParties('agents', index).map(({ _id, fullName, type }) => (
                                                        <option key={_id} value={_id}>
                                                            {fullName} ({type})
                                                        </option>
                                                    ))}
                                                </select>
                                                {!isReadOnly && formData?.agents?.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => deleteItemAtIndex('agents', index)}
                                                        className="h-7 w-7 text-rose-600 hover:text-rose-700 border border-slate-300 rounded-none bg-white flex items-center justify-center cursor-pointer text-sm"
                                                        title="Delete"
                                                    >
                                                        <IoIosRemove />
                                                    </button>
                                                )}
                                            </div>
                                        )
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="mb-2 flex-1">
                        <label className="block text-[11px] font-medium text-slate-700 mb-0.5">Remarks</label>
                        <textarea
                            value={formData.remarks || ''}
                            onChange={(e) => handleInputChange('remarks', e.target.value)}
                            rows={4}
                            className="w-full px-2 py-1 scroll-thin border border-slate-300 bg-white text-slate-800 rounded-none text-[11px] font-medium resize-y focus:outline-none focus:border-[#313C4C] min-h-[75px]"
                            placeholder="Enter remarks..."
                        />
                    </div>
                    <div>
                        <div className="bg-slate-50 p-2 rounded-none border border-slate-200 w-full text-[11px] text-slate-600">
                            <div className="flex items-center justify-end gap-1">
                                <p className="text-rose-600 font-medium m-0">*</p>
                                <p className="text-[11px] font-medium text-slate-600 m-0">
                                    <span className="font-medium text-slate-800">Note:</span> This will be saved as a draft request. You can edit it later before posting.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </CustomAddModal>

            <CustomShowModal
                open={open?.isWarning}
                handleClose={() => setOpen({ isWarning: false })}
                maxWidth="md"
                title="Confirm Posting"
            >
                <div className="p-4 text-[11px] font-medium">
                    {/* Icon and Message */}
                    <div className="flex flex-col items-center mb-4 text-center">
                        <div className="w-10 h-10 bg-amber-50 border border-amber-200 rounded-none flex items-center justify-center mb-2">
                            <IoWarningOutline className="w-5 h-5 text-amber-600" />
                        </div>

                        <h3 className="text-[12px] font-medium text-slate-900 mb-1">
                            Are you sure you want to post this request?
                        </h3>
                        <p className="text-slate-600 text-[11px] font-medium m-0">
                            Once posted, the request cannot be edited and will be sent to the Fuel Supply Team.
                        </p>
                    </div>

                    {/* Warning Box */}
                    <div className="bg-amber-50/70 border-l-2 border-amber-400 rounded-none p-2 mb-4">
                        <div className="flex items-center gap-2">
                            <IoAlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                            <div>
                                <p className="text-[11px] font-medium text-amber-900 m-0">
                                    Important Notice
                                </p>
                                <p className="text-[11px] font-medium text-amber-800 mt-0.5 m-0">
                                    This action cannot be undone. Please review the request details before proceeding.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 justify-end">
                        <button
                            onClick={() => setOpen({ isWarning: false })}
                            className="h-7 px-3 text-[11px] font-medium text-slate-700 bg-slate-100 rounded-none border border-slate-300 hover:bg-slate-200 transition cursor-pointer"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => handlePost()}
                            className="h-7 px-3 text-[11px] font-medium text-white bg-[#313C4C] rounded-none hover:bg-[#1e2632] transition shadow-2xs cursor-pointer"
                        >
                            Post Request
                        </button>
                    </div>
                </div>
            </CustomShowModal>
        </div>
    );
};

export default FuelSupplyReqModel;
